import React, { useState, useRef, useEffect } from "react";
import { Text } from "@core/index";
import { motion, AnimatePresence } from "framer-motion";
// Color combinations array with background, border, and text colors for both light and dark modes
const colorCombinations = [
  "bg-purple-200 border-purple-300 text-purple-700 dark:bg-purple-800/40 dark:border-purple-600 dark:text-purple-300",
  "bg-green-200 border-green-300 text-green-700 dark:bg-green-800/40 dark:border-green-600 dark:text-green-300",
  "bg-red-200 border-red-300 text-red-700 dark:bg-red-800/40 dark:border-red-600 dark:text-red-300",
  "bg-blue-200 border-blue-300 text-blue-700 dark:bg-blue-800/40 dark:border-blue-600 dark:text-blue-300",
  "bg-yellow-200 border-yellow-300 text-yellow-700 dark:bg-yellow-800/40 dark:border-yellow-600 dark:text-yellow-300",
];

// Base highlight classes without color-specific classes
const baseHighlightClasses = `
  inline-block px-1 mx-0.5 rounded-md 
  border
  transition-all duration-200 ease-in-out
`;

export interface CustomPromptInputProps {
  value: string;
  onChange: (value: string) => void;
  highlightedWords: { word: string; alternatives: string[] }[];
  placeholder?: string;
  disabled?: boolean;
  maxLength?: number;
  dataTestId?: string;
  isEnhancing?: boolean;
}

export const CustomPromptInput: React.FC<CustomPromptInputProps> = ({
  value,
  onChange,
  highlightedWords,
  placeholder,
  disabled = false,
  maxLength = 300,
  dataTestId,
  isEnhancing = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [dropdown, setDropdown] = useState<{
    word: string;
    rect: { top: number; left: number };
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Add word replacement function
  const replaceWord = (oldWord: string, newWord: string) => {
    // Find the highlighted word group that contains either the old word or the new word
    const wordGroup = highlightedWords.find(
      (hw) =>
        hw.word.toLowerCase() === oldWord.toLowerCase() ||
        hw.word.toLowerCase() === newWord.toLowerCase() ||
        hw.alternatives.some(
          (alt) =>
            alt.toLowerCase() === oldWord.toLowerCase() ||
            alt.toLowerCase() === newWord.toLowerCase(),
        ),
    );

    if (wordGroup) {
      // Create regex that matches the old word or any of its alternatives
      const allPossibleWords = [wordGroup.word, ...wordGroup.alternatives];
      const regex = new RegExp(`\\b(${allPossibleWords.join("|")})\\b`, "i");

      // Replace the matched word with the new word
      const newValue = value.replace(regex, newWord);
      onChange(newValue);
      setDropdown(null);
    }
  };

  // Split text into segments (words and highlights)
  const getTextSegments = () => {
    const segments: {
      text: string;
      isHighlighted: boolean;
      originalWord?: string;
      alternatives?: string[];
    }[] = [];
    let remainingText = value;

    // Create an array of all words to highlight (main words + alternatives)
    const allHighlightWords = highlightedWords.reduce(
      (acc, { word, alternatives }) => {
        return [
          ...acc,
          { text: word, originalWord: word, alternatives },
          ...alternatives.map((alt) => ({
            text: alt,
            originalWord: word,
            alternatives,
          })),
        ];
      },
      [] as { text: string; originalWord: string; alternatives: string[] }[],
    );

    // Sort by length (longest first) to prevent partial matches
    allHighlightWords.sort((a, b) => b.text.length - a.text.length);

    while (remainingText.length > 0) {
      let matched = false;

      for (const { text, originalWord, alternatives } of allHighlightWords) {
        const regex = new RegExp(`\\b${text}\\b`, "i");
        const match = remainingText.match(regex);

        if (match && match.index === 0) {
          segments.push({
            text: match[0],
            isHighlighted: true,
            originalWord,
            alternatives,
          });
          remainingText = remainingText.slice(match[0].length);
          matched = true;
          break;
        }
      }

      if (!matched) {
        // Find the next potential highlight
        const nextHighlight = allHighlightWords
          .map((hw) => {
            const regex = new RegExp(`\\b${hw.text}\\b`, "i");
            const match = remainingText.match(regex);
            return match
              ? { index: match.index, length: match[0].length }
              : null;
          })
          .filter(Boolean)
          .sort((a, b) => (a?.index || 0) - (b?.index || 0))[0];

        if (nextHighlight) {
          // Add text before the next highlight
          segments.push({
            text: remainingText.slice(0, nextHighlight.index),
            isHighlighted: false,
          });
          remainingText = remainingText.slice(nextHighlight.index);
        } else {
          // No more highlights, add remaining text
          segments.push({
            text: remainingText,
            isHighlighted: false,
          });
          remainingText = "";
        }
      }
    }

    return segments;
  };

  const handleContainerClick = () => {
    if (!disabled && !isEnhancing) {
      setIsEditing(true);
      // Add a small delay to ensure the textarea is rendered
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
  };

  // Update the textarea onChange handler
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (newValue.length <= maxLength) {
      onChange(newValue);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div
        data-testid="imagen-prompt-div"
        className={`relative min-h-[120px] rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white/60 dark:bg-gray-900/60 cursor-text transition-all duration-300 ${
          isEditing ? "ring-2 ring-purple-400" : "hover:border-purple-400"
        } ${disabled || isEnhancing ? "opacity-50 cursor-not-allowed pointer-events-none" : ""}`}
        onClick={(e) => {
          if (!(e.target as HTMLElement).closest(".dropdown-container")) {
            setDropdown(null);
          }
          if (!dropdown) {
            handleContainerClick();
          }
        }}
      >
        {/* Editable textarea (only visible when editing) */}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setIsEditing(false)}
          maxLength={maxLength}
          disabled={disabled}
          data-testid={dataTestId}
          className={`w-full resize-none rounded-xl border-2 border-gray-200 dark:border-gray-600 p-4 text-base focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-dark-500 text-gray-900 dark:text-gray-100 transition-all duration-200 ${
            isEditing ? "block" : "hidden"
          }`}
          placeholder={placeholder}
          style={{
            minHeight: "120px",
            height: `${Math.max(120, (value.match(/\n/g) || []).length * 24 + 120)}px`,
          }}
        />

        {/* Display div (only visible when not editing) */}
        {!isEditing && (
          <div
            className={`w-full min-h-[120px] rounded-xl border-2 border-gray-200 dark:border-gray-600 p-4 text-base bg-white dark:bg-dark-500 text-gray-900 dark:text-gray-100 cursor-text transition-all duration-200 ${
              disabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={() => !disabled && setIsEditing(true)}
          >
            {value ? (
              getTextSegments().map((segment, index) =>
                segment.isHighlighted ? (
                  <span
                    key={index}
                    className={`${baseHighlightClasses} ${
                      colorCombinations[
                        Math.abs(segment.text.charCodeAt(0)) %
                          colorCombinations.length
                      ]
                    } cursor-pointer hover:scale-[1.02]`}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent triggering the container's onClick
                      const rect = e.currentTarget.getBoundingClientRect();
                      const container =
                        containerRef.current?.getBoundingClientRect();
                      if (container) {
                        setDropdown({
                          word: segment.originalWord,
                          rect: {
                            top: rect.bottom - container.top,
                            left: rect.left - container.left,
                          },
                        });
                      }
                    }}
                  >
                    {segment.text} ▼
                  </span>
                ) : (
                  <span key={index}>{segment.text}</span>
                ),
              )
            ) : (
              <Text
                variant="p"
                className="text-gray-500 dark:text-gray-400"
                size={"base"}
              >
                {placeholder}
              </Text>
            )}
          </div>
        )}

        {/* Loading Animation Overlay */}
        <AnimatePresence>
          {isEnhancing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 rounded-xl overflow-hidden backdrop-blur-md z-20"
            >
              {/* Dynamic gradient background with radial fade */}
              <motion.div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 rounded-full bg-[conic-gradient(at_center,#FF61D8,#6B8DE3,#39E5B6,#FCE53D,#FF61D8)] opacity-70 animate-spin-slow"
                animate={{
                  scale: [1, 1.1, 1],
                  background: [
                    "conic-gradient(at center, #FF61D8, #6B8DE3, #39E5B6, #FCE53D, #FF61D8)",
                    "conic-gradient(at center, #6B8DE3, #39E5B6, #FCE53D, #FF61D8, #6B8DE3)",
                    "conic-gradient(at center, #39E5B6, #FCE53D, #FF61D8, #6B8DE3, #39E5B6)",
                  ],
                }}
                style={{
                  maskImage:
                    "radial-gradient(circle at center, black, transparent)",
                  WebkitMaskImage:
                    "radial-gradient(circle at center, black, transparent)",
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                {/* Animated mask size */}
                <motion.div
                  className="absolute inset-0 rounded-full"
                  animate={{
                    background: [
                      "radial-gradient(circle at center, black 40%, transparent 60%)",
                      "radial-gradient(circle at center, black 30%, transparent 70%)",
                      "radial-gradient(circle at center, black 40%, transparent 60%)",
                    ],
                  }}
                  style={{
                    maskImage:
                      "radial-gradient(circle at center, black, transparent)",
                    WebkitMaskImage:
                      "radial-gradient(circle at center, black, transparent)",
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
              </motion.div>

              {/* Glass effect overlay with dynamic radial emphasis */}
              <motion.div
                className="absolute inset-0 backdrop-blur-[8px] mix-blend-soft-light"
                animate={{
                  background: [
                    "radial-gradient(circle at center, transparent 40%, rgba(255,255,255,0.07) 60%)",
                    "radial-gradient(circle at center, transparent 30%, rgba(255,255,255,0.05) 70%)",
                    "radial-gradient(circle at center, transparent 40%, rgba(255,255,255,0.07) 60%)",
                  ],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              {/* Energetic shapes */}
              <motion.div
                className="absolute w-48 h-48 rounded-full bg-gradient-to-r from-fuchsia-500/60 to-purple-600/60 mix-blend-soft-light"
                animate={{
                  x: ["0%", "60%", "0%"],
                  y: ["0%", "60%", "0%"],
                  scale: [1, 1.5, 1],
                  rotate: [0, 360, 0],
                }}
                transition={{
                  duration: 12,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
              <motion.div
                className="absolute w-56 h-56 rounded-full bg-gradient-to-r from-cyan-400/60 to-blue-600/60 mix-blend-soft-light"
                animate={{
                  x: ["80%", "20%", "80%"],
                  y: ["20%", "80%", "20%"],
                  scale: [1.2, 0.8, 1.2],
                  rotate: [180, -180, 180],
                }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
              <motion.div
                className="absolute w-52 h-52 rounded-full bg-gradient-to-r from-amber-400/60 to-orange-600/60 mix-blend-soft-light"
                animate={{
                  x: ["40%", "90%", "40%"],
                  y: ["90%", "30%", "90%"],
                  scale: [0.8, 1.4, 0.8],
                  rotate: [45, 315, 45],
                }}
                transition={{
                  duration: 11,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              {/* Energetic particles */}
              {[...Array(12)].map((_, i) => (
                <motion.div
                  key={i}
                  className={`absolute w-2 h-2 rounded-full ${
                    [
                      "bg-pink-400/80",
                      "bg-purple-400/80",
                      "bg-blue-400/80",
                      "bg-green-400/80",
                      "bg-yellow-400/80",
                      "bg-orange-400/80",
                    ][i % 6]
                  }`}
                  initial={{
                    x: `${Math.random() * 100}%`,
                    y: `${Math.random() * 100}%`,
                    scale: 0,
                  }}
                  animate={{
                    x: `${Math.random() * 100}%`,
                    y: `${Math.random() * 100}%`,
                    scale: [0, 1.5, 0],
                  }}
                  transition={{
                    duration: 2 + Math.random() * 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: i * 0.1,
                  }}
                />
              ))}

              {/* Rainbow shimmer */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                animate={{
                  x: ["-100%", "100%"],
                  background: [
                    "linear-gradient(to right, transparent, rgba(255,105,180,0.2), transparent)",
                    "linear-gradient(to right, transparent, rgba(147,112,219,0.2), transparent)",
                    "linear-gradient(to right, transparent, rgba(64,224,208,0.2), transparent)",
                  ],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Dropdown */}
        {dropdown && (
          <div
            className="absolute z-10 min-w-[120px] 
              bg-white dark:bg-gray-800 
              rounded-lg shadow-lg 
              border border-purple-200 dark:border-purple-700
              p-1 backdrop-blur-sm
              animate-fadeIn dropdown-container"
            style={{
              top: `${dropdown.rect.top + 5}px`,
              left: `${dropdown.rect.left}px`,
            }}
            onClick={(e) => e.stopPropagation()} // Prevent container click
          >
            {highlightedWords
              .find(
                (hw) => hw.word.toLowerCase() === dropdown.word.toLowerCase(),
              )
              ?.alternatives.map((alt, i) => (
                <div
                  key={i}
                  className="m-1 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent container click
                    replaceWord(dropdown.word, alt);
                  }}
                >
                  <span
                    className={`${baseHighlightClasses}
                      ${colorCombinations[i % colorCombinations.length]}
                      hover:scale-[1.02] hover:shadow-sm
                      active:scale-[0.98]
                      w-full block`}
                  >
                    {alt}
                  </span>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};
